/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ChevronRight from '@material-ui/icons/ChevronRight';
import classnames from 'classnames';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';

export interface IconRadioLabelProps {
  label: string | JSX.Element;
  onIconClick?: MouseEventHandler<HTMLDivElement>;
  icon?: JSX.Element;
}

const useStyles = makeStyles(theme => ({
  radio : {
    display        : 'flex',
    justifyContent : 'space-between',
    padding        : '5px 0px',
    maxWidth       : 'calc(100vw - 104px)'
  },
  radioLabel : toggleThemeStyle({
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  }, {
    color          : dlsColor('grey1'),
    display        : 'flex',
    fontSize       : '14px',
    alignItems     : 'center',
    fontFamily     : 'Inter',
    fontWeight     : 500,
    lineHeight     : '16px',
    letterSpacing  : '0px',
    justifyContent : 'center'
  }),
  currentSelectedLabel : {
    '.MuiFormControlLabel-root:has(.Mui-checked) &' : {
      fontWeight : 600
    }
  },
  radioChevron : {
    display    : 'flex',
    alignItems : 'center'
  }
}));

/**
 * @IconRadioLabel
 *
 * Abstracted Radio Label that allow for a user to pass
 * a clickable icon as the label.
 *
 * ```typescript
 * import {IconRadioLabel} from '@ion/dls/src/components/radio';
 *
 * <IconRadioLabel label='Choose me'
 *   onIconClick={handleIconClick}
 *   icon={<NewIcon />} />
 *
 * ```
 *
 * @param {string} label: radio label
 * @param {Function} onIconClick: callback for when icon is clicked
 * @param {JSX.Element} icon: overrides default chevron as radio labels icon
 * @returns {ReactNode}
 */
export const IconRadioLabel = ({
  label,
  onIconClick,
  icon = <ChevronRight />
}: IconRadioLabelProps) => {
  const classes = useStyles();

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  return (
    <div className={classes.radio}>
      <span className={classnames(classes.radioLabel, {
        [classes.currentSelectedLabel] : isCurrentTheme
      })}>
        {label}
      </span>
      {
        onIconClick &&
          <div className={classes.radioChevron}
            onClick={onIconClick}>
            {icon}
          </div>
      }
    </div>
  );
};

export default IconRadioLabel;
